// Pure overlap-resolution helpers for blocks and expanded group frames.
// Used by WorkflowCanvas to push items off each other during drag, so the
// user never ends up with overlapping nodes. Geometry is center-origin
// (matches WorkflowBlock.x/y semantics in src/types/workflow.ts).

import type { WorkflowBlock, BlockGroup, BlockDimensions } from '@/types/workflow';

const BLOCK_W = 200;
const BLOCK_H = 100;
const PADDING = 20;        // gap kept between blocks
const GROUP_PADDING = 40;  // gap kept between sibling expanded groups
const STEP = 20;           // grid step (matches GRID_SIZE convention)
const MAX_RING = 40;       // up to 40 rings × 20px = 800px search radius

export interface Rect { left: number; top: number; right: number; bottom: number }

export function blockRect(b: { x: number; y: number }, w = BLOCK_W, h = BLOCK_H, pad = 0): Rect {
  return {
    left: b.x - w / 2 - pad,
    top: b.y - h / 2 - pad,
    right: b.x + w / 2 + pad,
    bottom: b.y + h / 2 + pad,
  };
}

export function rectsOverlap(a: Rect, b: Rect): boolean {
  return !(a.right <= b.left || b.right <= a.left || a.bottom <= b.top || b.bottom <= a.top);
}

function snap(n: number, step = STEP): number {
  return Math.round(n / step) * step;
}

export interface ResolveBlockOptions {
  /** Block being moved (id excluded from collision check). */
  blockId: string;
  /** Requested center position. */
  x: number;
  y: number;
  /** Block dimensions if known (otherwise default 200×100). */
  width?: number;
  height?: number;
  padding?: number;
  /** Live dimensions för ANDRA block (mätt via ResizeObserver i
   *  WorkflowBlock). Utan detta antas alla andra block vara 200×100,
   *  vilket gör att tall-blocks med markdown-content hamnar i falsk-
   *  positiva överlapp och spiral-sök:en flyttar dem fel-axel. */
  otherDimensions?: Record<string, BlockDimensions>;
}

/**
 * Find the nearest non-overlapping snapped position for a block. Spirals
 * out from the requested cell on the 20px grid until a clear cell is
 * found. Falls back to the requested position if MAX_RING is exhausted
 * (extremely dense canvas — better to allow than freeze the drag).
 */
export function resolveBlockPosition(
  allBlocks: WorkflowBlock[],
  opts: ResolveBlockOptions,
): { x: number; y: number } {
  const w = opts.width ?? BLOCK_W;
  const h = opts.height ?? BLOCK_H;
  const pad = opts.padding ?? PADDING;
  const sx = snap(opts.x);
  const sy = snap(opts.y);

  const others = allBlocks.filter(b => b.id !== opts.blockId);
  const otherRects = others.map(b => {
    const d = opts.otherDimensions?.[b.id];
    return blockRect(b, d?.width ?? BLOCK_W, d?.height ?? BLOCK_H, 0);
  });

  const clearAt = (cx: number, cy: number) => {
    const r = blockRect({ x: cx, y: cy }, w, h, pad);
    for (const or of otherRects) {
      if (rectsOverlap(r, or)) return false;
    }
    return true;
  };

  if (clearAt(sx, sy)) return { x: sx, y: sy };

  // Spiral search — try cells at increasing chebyshev distance.
  for (let ring = 1; ring <= MAX_RING; ring++) {
    const offset = ring * STEP;
    // Try the 8 cardinal/diagonal directions first (cheapest displacement).
    const candidates: Array<[number, number]> = [
      [sx + offset, sy], [sx - offset, sy],
      [sx, sy + offset], [sx, sy - offset],
      [sx + offset, sy + offset], [sx - offset, sy + offset],
      [sx + offset, sy - offset], [sx - offset, sy - offset],
    ];
    // Then ring perimeter (skip the corners we already tried).
    for (let d = STEP; d < offset; d += STEP) {
      candidates.push(
        [sx + offset, sy + d], [sx + offset, sy - d],
        [sx - offset, sy + d], [sx - offset, sy - d],
        [sx + d, sy + offset], [sx - d, sy + offset],
        [sx + d, sy - offset], [sx - d, sy - offset],
      );
    }
    for (const [cx, cy] of candidates) {
      if (clearAt(cx, cy)) return { x: cx, y: cy };
    }
  }
  return { x: sx, y: sy };
}

export interface GroupBoundsRect {
  left: number; top: number; right: number; bottom: number;
}

/**
 * Returns a translation (dx, dy) to apply to a moving group's bounds so
 * it no longer overlaps any sibling expanded group at the same nesting
 * level. Returns {dx:0, dy:0} when no overlap exists.
 *
 * Strategy: for each overlapping sibling, compute the minimal axis-aligned
 * push needed to clear it; pick the push with the smallest magnitude.
 * Iterates up to 8 times so multi-sibling stacks settle.
 */
export function resolveGroupOverlap(
  movingId: string,
  movingBounds: GroupBoundsRect,
  allGroups: BlockGroup[],
): { dx: number; dy: number } {
  const moving = allGroups.find(g => g.id === movingId);
  if (!moving) return { dx: 0, dy: 0 };
  const parentId = moving.parentGroupId;

  // Only collide with sibling EXPANDED groups (collapsed groups render as
  // small cards and use block-level overlap rules instead).
  const siblings = allGroups.filter(g =>
    g.id !== movingId
    && !g.collapsed
    && (g.parentGroupId ?? undefined) === (parentId ?? undefined)
    && g.boundsLeft != null && g.boundsTop != null && g.boundsRight != null && g.boundsBottom != null,
  );
  if (siblings.length === 0) return { dx: 0, dy: 0 };

  let dx = 0, dy = 0;
  let bounds = { ...movingBounds };

  for (let iter = 0; iter < 8; iter++) {
    let collided = false;
    for (const s of siblings) {
      const sb: GroupBoundsRect = {
        left: s.boundsLeft! - GROUP_PADDING,
        top: s.boundsTop! - GROUP_PADDING,
        right: s.boundsRight! + GROUP_PADDING,
        bottom: s.boundsBottom! + GROUP_PADDING,
      };
      if (
        bounds.right <= sb.left || sb.right <= bounds.left
        || bounds.bottom <= sb.top || sb.bottom <= bounds.top
      ) continue;

      // Overlap. Compute the four possible pushes; pick smallest magnitude.
      const pushRight = sb.right - bounds.left;   // move right
      const pushLeft  = bounds.right - sb.left;   // move left  (apply as -)
      const pushDown  = sb.bottom - bounds.top;   // move down
      const pushUp    = bounds.bottom - sb.top;   // move up    (apply as -)
      const min = Math.min(pushRight, pushLeft, pushDown, pushUp);
      let stepX = 0, stepY = 0;
      if (min === pushRight) stepX = pushRight;
      else if (min === pushLeft) stepX = -pushLeft;
      else if (min === pushDown) stepY = pushDown;
      else stepY = -pushUp;
      // Snap push to grid so blocks inside stay grid-aligned.
      stepX = snap(stepX);
      stepY = snap(stepY);
      if (stepX === 0 && stepY === 0) {
        stepX = stepX || (min === pushRight ? STEP : -STEP);
      }
      dx += stepX;
      dy += stepY;
      bounds = {
        left: bounds.left + stepX,
        right: bounds.right + stepX,
        top: bounds.top + stepY,
        bottom: bounds.bottom + stepY,
      };
      collided = true;
    }
    if (!collided) break;
  }
  return { dx, dy };
}
